import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useSWR from 'swr';
import { Layout, LoadingSpinner, ErrorMessage, ConfirmDialog } from '@/shared/components';
import { FileUploadDropzone } from '@/shared/ui';
import { GalleryManager } from '@/features/gallery';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { indexPageSettingsApi } from '@/shared/api';
import { FILE_UPLOAD_CONFIG } from '@/shared/config/api'; // For ALLOWED_IMAGE_TYPES
import {
  indexPageSettingsSchema,
  type IndexPageSettingsFormData
} from '@/shared/schemas';

const HERO_IMAGE_MAX_SIZE = 1 * 1024 * 1024; // 1MB

export const IndexPageSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageError, setImageError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const {
    data: settingsResponse,
    error: loadError,
    isLoading,
    mutate
  } = useSWR(
    'index-page-settings',
    () => indexPageSettingsApi.get(),
    { revalidateOnFocus: false }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<IndexPageSettingsFormData>({
    resolver: zodResolver(indexPageSettingsSchema),
    defaultValues: { reviewsWidgetCode: '' }
  });

  useEffect(() => {
    if (settingsResponse?.success && settingsResponse.settings) {
      reset({
        reviewsWidgetCode: settingsResponse.settings.reviewsWidgetCode ?? ''
      });
    }
  }, [settingsResponse, reset]);

  const { isSubmitting, handleSubmit: handleFormSubmit } =
    useFormSubmission<IndexPageSettingsFormData>({
      onSubmit: async (data: IndexPageSettingsFormData) => {
        const response = await indexPageSettingsApi.update({
          reviewsWidgetCode: data.reviewsWidgetCode?.trim() || null
        });
        if (response.success) {
          mutate();
        }
      },
      successMessage: 'Настройки успешно сохранены',
      errorMessage: 'Ошибка при сохранении настроек'
    });

  const handleBack = (): void => {
    navigate('/pages');
  };

  const validateImageFile = (file: File): string | null => {
    if (file.size > HERO_IMAGE_MAX_SIZE) {
      return 'Размер файла не должен превышать 1MB';
    }
    if (
      !FILE_UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES.includes(
        file.type as (typeof FILE_UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES)[number]
      )
    ) {
      return 'Разрешены только файлы JPG, PNG и WebP';
    }
    return null;
  };

  const handleImageSelect = async (files: File[]): Promise<void> => {
    const file = files[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      setImageError(validationError);
      return;
    }

    setIsUploadingImage(true);
    setImageError(null);
    setUploadProgress(0);

    try {
      await indexPageSettingsApi.uploadHeroImage(file, (event) => {
        const progress = event.total
          ? Math.round((event.loaded * 100) / event.total)
          : 0;
        setUploadProgress(progress);
      });
      mutate();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Ошибка загрузки изображения';
      setImageError(errorMessage);
    } finally {
      setIsUploadingImage(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteImage = async (): Promise<void> => {
    try {
      await indexPageSettingsApi.deleteHeroImage();
      mutate();
      setDeleteDialogOpen(false);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Ошибка удаления изображения';
      setImageError(errorMessage);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading) {
    return (
      <Layout title="Загрузка...">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" text="Загрузка настроек..." />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (loadError || !settingsResponse?.success) {
    return (
      <Layout title="Ошибка">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <ErrorMessage message="Ошибка загрузки настроек. Попробуйте обновить страницу." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Настройки главной страницы">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-base-content/60 mt-1">
              Управление виджетами и контентом главной страницы
            </p>
          </div>
          <button
            onClick={handleBack}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            <i className="fas fa-arrow-left"></i>
            Назад к списку
          </button>
        </div>

        {/* Hero Image Section */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-6">
            <h3 className="text-lg font-semibold text-base-content mb-4">
              <i className="fas fa-image mr-2 text-primary"></i>
              Главное изображение
            </h3>

            {imageError && (
              <div className="mb-4">
                <ErrorMessage message={imageError} onDismiss={() => setImageError(null)} />
              </div>
            )}

            {settingsResponse?.settings?.heroImageUrl ? (
              <div className="space-y-4">
                <div className="relative group inline-block max-w-2xl cursor-pointer" onClick={() => setDeleteDialogOpen(true)}>
                  <img
                    src={settingsResponse.settings.heroImageUrl}
                    alt="Главное изображение"
                    className="w-full rounded-lg shadow-md transition-all duration-200 group-hover:brightness-75"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <button
                      type="button"
                      className="btn btn-error"
                      disabled={isUploadingImage}
                    >
                      <i className="fas fa-trash mr-2"></i>
                      Удалить
                    </button>
                  </div>
                </div>
                <p className="text-sm text-base-content/60">
                  Наведите на изображение, чтобы удалить. Загрузите новое изображение, чтобы заменить текущее.
                </p>
              </div>
            ) : (
              <p className="text-base-content/60 mb-4">
                Изображение не установлено. Будет использоваться стандартное изображение.
              </p>
            )}

            <div className="mt-4">
              <FileUploadDropzone
                onFileSelect={handleImageSelect}
                accept="image/jpeg,image/jpg,image/png,image/webp"
                multiple={false}
                maxSize={HERO_IMAGE_MAX_SIZE}
                disabled={isUploadingImage}
                dropzoneText="Перетащите изображение сюда или нажмите для выбора"
                helpText="Разрешены файлы JPG, PNG, WebP. Максимальный размер: 1MB. Рекомендуемый размер: 1600x800 пикселей."
              />

              {isUploadingImage && (
                <div className="flex items-center justify-center gap-2 mt-4 p-3 bg-primary/10 rounded-lg">
                  <LoadingSpinner size="sm" />
                  <span className="text-sm text-primary font-medium">
                    Загрузка... {uploadProgress}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        <GalleryManager />

        {/* Reviews Widget Section */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-6">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-6"
            >
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium flex items-center gap-2">
                    <i className="fas fa-star text-primary"></i>
                    Виджет отзывов
                  </span>
                </label>
                <textarea
                  {...register('reviewsWidgetCode')}
                  className={`textarea textarea-bordered w-full font-mono text-sm ${errors.reviewsWidgetCode ? 'textarea-error' : ''}`}
                  rows={10}
                  placeholder="Вставьте HTML/JS код виджета отзывов (например, Яндекс.Карты)..."
                  disabled={isSubmitting}
                />
                {errors.reviewsWidgetCode && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.reviewsWidgetCode.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    HTML/JS код виджета будет отображаться в секции отзывов на главной странице
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1 sm:flex-none"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Сохранение...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Сохранить настройки
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="btn btn-secondary flex-1 sm:flex-none"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times"></i>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteDialogOpen}
          title="Удалить изображение"
          message="Вы уверены, что хотите удалить главное изображение? Будет использоваться стандартное изображение."
          confirmText="Удалить"
          cancelText="Отмена"
          onConfirm={handleDeleteImage}
          onCancel={() => setDeleteDialogOpen(false)}
        />

      </div>
    </Layout>
  );
};
